/**
 * Simulação do motor de prazos para a vitrine da landing.
 *
 * Espelha as regras de `apps/api/src/modules/deadlines/engine` — CPC art. 224
 * (exclui o dia do começo), art. 219 (só dia útil conta), art. 220 (recesso de
 * 20/12 a 20/01) e os feriados nacionais fixos e móveis —, mas só com o
 * calendário nacional. Não é a fonte da verdade: o prazo de um processo é
 * calculado na API, com feriados locais e suspensões do tribunal.
 *
 * Mora aqui porque a landing não fala com a API nem carrega o CRM.
 */

export type ExclusionKind = 'weekend' | 'holiday' | 'recess';

export type DemoDayKind = 'before' | 'start' | 'counted' | 'due' | 'after' | ExclusionKind;

export interface DemoDay {
  /** Data civil `YYYY-MM-DD`. */
  date: string;
  day: number;
  month: number;
  kind: DemoDayKind;
  /** Posição na contagem (1…N) para dia útil contado. */
  count: number | null;
  /** Por que o dia não contou, ou o papel dele na contagem. */
  label: string | null;
}

export interface DeadlineSimulation {
  days: DemoDay[];
  dueDate: string;
  excluded: Record<ExclusionKind, number>;
  /** Feriados que caíram dentro da contagem, na ordem em que apareceram. */
  holidays: string[];
}

const FIXED_HOLIDAYS: ReadonlyArray<readonly [month: number, day: number, label: string]> = [
  [1, 1, 'Confraternização Universal'],
  [4, 21, 'Tiradentes'],
  [5, 1, 'Dia do Trabalho'],
  [9, 7, 'Independência'],
  [10, 12, 'Nossa Senhora Aparecida'],
  [11, 2, 'Finados'],
  [11, 15, 'Proclamação da República'],
  [11, 20, 'Consciência Negra'],
  [12, 25, 'Natal'],
];

function toDate(date: string): Date {
  return new Date(`${date}T00:00:00.000Z`);
}

function toCivil(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function addDays(date: string, days: number): string {
  const base = toDate(date);
  base.setUTCDate(base.getUTCDate() + days);
  return toCivil(base);
}

/** Domingo de Páscoa (Meeus/Jones/Butcher) — âncora dos feriados móveis. */
function easterSunday(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return toCivil(new Date(Date.UTC(year, month - 1, day)));
}

export function holidayLabel(date: string): string | null {
  const [year = 0, month = 0, day = 0] = date.split('-').map(Number);

  const fixed = FIXED_HOLIDAYS.find(([m, d]) => m === month && d === day);
  if (fixed) return fixed[2];

  const easter = easterSunday(year);
  const movable: ReadonlyArray<readonly [offset: number, label: string]> = [
    [-48, 'Segunda-feira de Carnaval'],
    [-47, 'Terça-feira de Carnaval'],
    [-2, 'Sexta-feira Santa'],
    [60, 'Corpus Christi'],
  ];

  return movable.find(([offset]) => addDays(easter, offset) === date)?.[1] ?? null;
}

function isRecess(date: string): boolean {
  const [, month = 0, day = 0] = date.split('-').map(Number);
  return (month === 12 && day >= 20) || (month === 1 && day <= 20);
}

function isWeekend(date: string): boolean {
  const weekday = toDate(date).getUTCDay();
  return weekday === 0 || weekday === 6;
}

/**
 * O motivo que a vitrine mostra. Um dia pode ter mais de um (Natal dentro do
 * recesso); aqui vence o que explica melhor o desenho do calendário — o
 * recesso pinta um bloco contínuo, e o feriado só aparece quando é ele que
 * empurra a data.
 */
function exclusionOf(date: string): { kind: ExclusionKind; label: string } | null {
  if (isRecess(date)) return { kind: 'recess', label: 'Recesso forense' };

  const holiday = holidayLabel(date);
  if (holiday) return { kind: 'holiday', label: holiday };

  if (isWeekend(date)) return { kind: 'weekend', label: 'Fim de semana' };

  return null;
}

/**
 * Conta `businessDays` dias úteis a partir do dia seguinte à publicação e
 * devolve o calendário de `weeks` semanas, começando na segunda-feira da
 * semana da publicação, já classificado dia a dia.
 */
export function simulateDeadline(
  startDate: string,
  businessDays: number,
  weeks = 9,
): DeadlineSimulation {
  const counts = new Map<string, number>();
  const exclusions = new Map<string, { kind: ExclusionKind; label: string }>();
  const excluded: Record<ExclusionKind, number> = { weekend: 0, holiday: 0, recess: 0 };
  const holidays: string[] = [];

  let cursor = startDate;
  let counted = 0;

  while (counted < businessDays) {
    cursor = addDays(cursor, 1);
    const exclusion = exclusionOf(cursor);

    if (exclusion) {
      exclusions.set(cursor, exclusion);
      excluded[exclusion.kind] += 1;
      if (exclusion.kind === 'holiday') holidays.push(exclusion.label);
      continue;
    }

    counted += 1;
    counts.set(cursor, counted);
  }

  const dueDate = cursor;
  const mondayOffset = (toDate(startDate).getUTCDay() + 6) % 7;
  const firstDay = addDays(startDate, -mondayOffset);

  const days = Array.from({ length: weeks * 7 }, (_, index): DemoDay => {
    const date = addDays(firstDay, index);
    const [, month = 0, day = 0] = date.split('-').map(Number);
    const count = counts.get(date) ?? null;
    const base = { date, day, month, count };

    if (date < startDate) return { ...base, kind: 'before', label: null };
    if (date === startDate) return { ...base, kind: 'start', label: 'Publicação' };
    if (date > dueDate) return { ...base, kind: 'after', label: null };
    if (date === dueDate) return { ...base, kind: 'due', label: 'Vencimento' };
    if (count !== null) return { ...base, kind: 'counted', label: null };

    const exclusion = exclusions.get(date);
    return { ...base, kind: exclusion?.kind ?? 'weekend', label: exclusion?.label ?? null };
  });

  return { days, dueDate, excluded, holidays };
}
